/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

const ACTION_BASE = process.env.REACT_APP_ACTION_URL || ''

let slotsEtag = null
let slotsCache = null

const bookingApi = (path, { headers, ...rest } = {}) =>
  fetch(`${ACTION_BASE}/booking-public${path}`, {
    ...rest,
    headers: { 'Content-Type': 'application/json', ...headers },
  }).then(res => res.ok ? res.json() : res.json().then(e => Promise.reject(e)))

const adminApi = (path, token, { headers, ...rest } = {}) =>
  fetch(`${ACTION_BASE}/booking-admin${path}`, {
    ...rest,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...headers,
    },
  }).then(async (res) => {
    if (res.ok) return res.json()
    let body
    try { body = await res.json() } catch { body = { error: `HTTP ${res.status}` } }
    return Promise.reject(body)
  })

export const bookingService = {
  purgeCache: (token) =>
    adminApi('/purge-cache', token, { method: 'POST' }),

  setFeatureFlags: (flags, token) =>
    adminApi('/feature-flags', token, {
      method: 'POST',
      body: JSON.stringify(flags),
    }),

  getSlots: async () => {
    const headers = { 'Content-Type': 'application/json' }
    if (slotsEtag) headers['If-None-Match'] = slotsEtag

    const res = await fetch(`${ACTION_BASE}/booking-public/slots`, { headers })

    if (res.status === 304 && slotsCache) return slotsCache

    if (!res.ok) {
      const err = await res.json()
      return Promise.reject(err)
    }

    const etag = res.headers.get('ETag')
    if (etag) slotsEtag = etag

    slotsCache = await res.json()
    return slotsCache
  },

  book: (slotId, token, { gondolaTitle, slotTime, speakerName, maxPublicSlots } = {}) =>
    bookingApi('/book', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ slotId, gondolaTitle, slotTime, speakerName, maxPublicSlots }),
    }),

  cancel: (bookingCode, token) =>
    bookingApi('/cancel', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ bookingCode }),
    }),

  getMyBookings: (token) =>
    bookingApi('/my-bookings', {
      headers: { Authorization: `Bearer ${token}` },
    }),

  getHostBookings: (token) => adminApi('/bookings', token),

  getUsers: (token) => adminApi('/users', token),

  assignSlot: (data, token) =>
    adminApi('/assign', token, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  generateQrToken: (gondolaId, slotTime, token, oldToken) =>
    adminApi('/generate-qr', token, {
      method: 'POST',
      body: JSON.stringify({ gondolaId, slotTime, ...(oldToken && { oldToken }) }),
    }),

  resetGondola: (gondolaId, token) =>
    adminApi('/reset-gondola', token, {
      method: 'POST',
      body: JSON.stringify({ gondolaId }),
    }),

  cancelGondola: (gondolaId, token) =>
    adminApi('/cancel-gondola', token, {
      method: 'POST',
      body: JSON.stringify({ gondolaId }),
    }),

  reactivateGondola: (gondolaId, token) =>
    adminApi('/reactivate-gondola', token, {
      method: 'POST',
      body: JSON.stringify({ gondolaId }),
    }),

  getGondolaStatus: (token) => adminApi('/gondola-status', token),

  checkin: (bookingCode, token) =>
    adminApi('/checkin', token, {
      method: 'POST',
      body: JSON.stringify({ bookingCode }),
    }),

  releaseSlot: (gondolaId, qrToken, authToken) =>
    adminApi('/release', authToken, {
      method: 'POST',
      body: JSON.stringify({ gondolaId, token: qrToken }),
    }),

  updateUserRole: (userSub, roles, authToken, speakerSlug) =>
    adminApi('/user-role', authToken, {
      method: 'POST',
      body: JSON.stringify({ userSub, roles, ...(speakerSlug != null && { speakerSlug }) }),
    }),

  getUsers: (authToken) =>
    adminApi('/users', authToken),

  speakerSync: (speakerPath, userId, authToken) =>
    adminApi('/speaker-sync', authToken, {
      method: 'POST',
      body: JSON.stringify({ speakerPath, userId }),
    }),

  speakerUnsync: (speakerPath, userId, authToken) =>
    adminApi('/speaker-sync', authToken, {
      method: 'POST',
      body: JSON.stringify({ speakerPath, userId, remove: true }),
    }),

  claim: (token, accessToken) =>
    bookingApi('/claim', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ token }),
    }),

  confirmUser: (username, authToken) =>
    adminApi('/users/confirm', authToken, {
      method: 'POST',
      body: JSON.stringify({ username }),
    }),
}
